import { FC, useEffect, useMemo } from "react";
import { useLocation, useRouteError } from "react-router-dom";

import { AppError } from "config/errors";
import { useAppSelector } from "store";
import { getUser } from "store/selectors/selectUser";
import { get as getApm } from "utils/elasticApm";

import AppErrorFallback from "./AppErrorFallback";

const AppErrorBoundary: FC = () => {
  const error = useRouteError();
  const location = useLocation();
  const userId = useAppSelector(state => getUser(state).id);
  const appError = useMemo(() => AppError.from(error), [error]);

  useEffect(() => {
    if (!appError.shouldReport) {
      return;
    }

    // Capture to APM, passing the original error so the throw-site stack
    // is preserved. Returns false if the APM singleton isn't ready yet.
    const report = () => {
      let apm: ReturnType<typeof getApm>;
      try {
        apm = getApm();
      } catch {
        return false;
      }

      if (userId) {
        apm.setUserContext({ id: userId });
      }
      apm.setCustomContext({ path: location.pathname });
      apm.captureError(error instanceof Error ? error : appError);
      return true;
    };

    // Retry on the next tick in case APM initializes after this effect runs
    // (e.g. an error thrown during the initial page load).
    if (!report()) {
      const retryTimer = setTimeout(report, 0);
      return () => clearTimeout(retryTimer);
    }
  }, [appError, error, location.pathname, userId]);

  const handleClick = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };

  return <AppErrorFallback onClick={handleClick} />;
};

export default AppErrorBoundary;
